/**
 * Pure helpers for the Costs tab service pie chart (partition, slices, SVG path).
 */

import type { CostBreakdownLine, CostBreakdownMeta, CostByAwsService } from '@/types/api';
import { formatCurrency } from '@/utils/format';

/** Target combined share for the aggregated "Other" slice (smallest services by cost). */
export const OTHER_SLICE_MIN_PCT = 10;
export const OTHER_SLICE_MAX_PCT = 15;

export const PIE_COLORS = [
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#ea580c',
  '#16a34a',
  '#0891b2',
  '#ca8a04',
  '#4f46e5',
  '#0d9488',
  '#e11d48',
  '#65a30d',
  '#525252',
] as const;

export interface PieSliceData {
  key: string;
  label: string;
  percent: number;
  cost: number;
  currency: string;
  color: string;
  /** One-line summary (service share of MTD, or Other headline). */
  headline: string;
  /** Cost Explorer sub-rows when the API includes them. */
  breakdown?: CostBreakdownLine[];
  /** Other slice only: per-service lines bundled into that slice. */
  bundledServiceLines?: string[];
  /** Other slice only: rows for enrichment status per bundled service. */
  bundledRows?: CostByAwsService[];
  /** API explanation when line items are missing or an error occurred. */
  breakdownMeta?: CostBreakdownMeta;
  /** Plain multi-line text (e.g. tests, accessibility snapshot). */
  tooltip: string;
}

// Re-export the extracted helper so existing callers (CostsAwsServiceTab,
// CostsByAppTab, costsTabPie.test.ts) don't need to update their imports.
// New callers should import directly from @/utils/format.
export { formatCurrency };

/**
 * Groups the smallest-cost services into "Other" so that slice is roughly 10–15% of MTD
 * when the data allows; otherwise uses the closest feasible grouping.
 */
export function partitionServicesForPieChart(services: CostByAwsService[]): {
  main: CostByAwsService[];
  other: CostByAwsService[];
} {
  if (services.length <= 2) {
    return { main: services, other: [] };
  }
  const asc = [...services].sort((a, b) => a.cost - b.cost);
  const other: CostByAwsService[] = [];
  let p = 0;

  for (const row of asc) {
    const nextP = p + row.percentOfTotal;
    if (p >= OTHER_SLICE_MIN_PCT && nextP > OTHER_SLICE_MAX_PCT) {
      break;
    }
    other.push(row);
    p = nextP;
    if (p >= OTHER_SLICE_MAX_PCT) {
      break;
    }
  }

  if (p < OTHER_SLICE_MIN_PCT) {
    const inOther = new Set(other.map((r) => r.awsService));
    for (const row of asc) {
      if (inOther.has(row.awsService)) {
        continue;
      }
      other.push(row);
      inOther.add(row.awsService);
      p += row.percentOfTotal;
      if (p >= OTHER_SLICE_MIN_PCT) {
        break;
      }
    }
  }

  const inOtherSet = new Set(other.map((r) => r.awsService));
  let main = services.filter((s) => !inOtherSet.has(s.awsService));

  if (main.length === 0) {
    const largest = asc[asc.length - 1];
    main = [largest];
    const idx = other.findIndex((r) => r.awsService === largest.awsService);
    if (idx >= 0) {
      other.splice(idx, 1);
    }
  }

  if (other.length === 1 && other[0].percentOfTotal > OTHER_SLICE_MAX_PCT) {
    return { main: services, other: [] };
  }

  return { main, other };
}

export function formatBreakdownTooltipLines(row: CostByAwsService, defaultCurrency: string): string[] {
  if (row.breakdown === undefined || row.breakdown.length === 0) {
    return [];
  }
  const lines: string[] = ['', 'Breakdown:'];
  for (const line of row.breakdown) {
    const cur = line.currency || defaultCurrency;
    const pct =
      line.percentOfParent !== undefined ? ` (${line.percentOfParent.toFixed(1)}%)` : '';
    lines.push(`  ${line.label}: ${formatCurrency(line.cost, cur)}${pct}`);
  }
  return lines;
}

export function buildPieSlices(
  main: CostByAwsService[],
  other: CostByAwsService[],
  defaultCurrency: string,
): PieSliceData[] {
  const slices: PieSliceData[] = [];
  const mainSorted = [...main].sort((a, b) => b.cost - a.cost);

  for (let i = 0; i < mainSorted.length; i++) {
    const row = mainSorted[i];
    const cur = row.currency || defaultCurrency;
    const headline = `${row.awsService}: ${formatCurrency(row.cost, cur)} (${row.percentOfTotal.toFixed(1)}% of MTD)`;
    const breakdown =
      row.breakdown !== undefined && row.breakdown.length > 0 ? row.breakdown : undefined;
    slices.push({
      key: row.awsService,
      label: row.awsService,
      percent: row.percentOfTotal,
      cost: row.cost,
      currency: cur,
      color: PIE_COLORS[i % PIE_COLORS.length],
      headline,
      breakdown,
      breakdownMeta: row.breakdownMeta,
      tooltip: [headline, ...formatBreakdownTooltipLines(row, defaultCurrency)].join('\n'),
    });
  }

  if (other.length > 0) {
    const otherPct = other.reduce((s, r) => s + r.percentOfTotal, 0);
    const otherCost = other.reduce((s, r) => s + r.cost, 0);
    const sortedOther = [...other].sort((a, b) => b.cost - a.cost);
    const bundledServiceLines = sortedOther.map((r) => {
      const c = r.currency || defaultCurrency;
      return `${r.awsService}: ${formatCurrency(r.cost, c)} (${r.percentOfTotal.toFixed(1)}%)`;
    });
    const headline = `Other — ${otherPct.toFixed(1)}% of month-to-date total`;
    slices.push({
      key: 'other',
      label: `Other (${other.length} ${other.length === 1 ? 'service' : 'services'})`,
      percent: otherPct,
      cost: otherCost,
      currency: defaultCurrency,
      color: '#94a3b8',
      headline,
      bundledServiceLines,
      bundledRows: sortedOther,
      tooltip: [headline, ...bundledServiceLines].join('\n'),
    });
  }

  return slices;
}

export function pieSlicePath(cx: number, cy: number, r: number, startAngle: number, sweepAngle: number): string {
  const x1 = cx + r * Math.cos(startAngle);
  const y1 = cy + r * Math.sin(startAngle);
  const x2 = cx + r * Math.cos(startAngle + sweepAngle);
  const y2 = cy + r * Math.sin(startAngle + sweepAngle);
  const largeArc = sweepAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}
